import type { FC } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useCrmStatus } from 'hooks/useCrmStatus';

import { Tooltip } from '../../../../components/Tooltip';
import type { Contact } from '../../../../domain/contact';
import { Crm } from '../../../../domain/crm';
import { translate } from '../../../../services/i18n';
import { CrmExportContainer } from '../CrmExportContainer';
import { StyledCrmExportButtonSmall, StyledCrmLink } from '../CrmExportContainer/styles';
import { InactiveCrmExportButton } from '../InactiveCrmExportButton';
import { NotConnectedCrmButton } from '../NotConnectedCrmButton';

import { useCrmActionsTracking } from './useCrmActionsTracking';

export const CrmExportActionsContainer: FC<{ contact: Contact }> = ({ contact }) => {
  const { isHbIntegrated, isSfIntegrated, isHbExportEnabled, isSfExportEnabled, isLoading } = useCrmStatus();

  const { handleCrmLinkTrack, handleEnableExportTrack } = useCrmActionsTracking();

  if (isLoading) {
    return null;
  }

  const { salesforceContactUrl, salesforceLeadUrl, hubspotContactUrl } = contact;

  if (salesforceContactUrl || salesforceLeadUrl) {
    return (
      <Tooltip placement="top-left" content={translate('contact_card.open_in_sf')} minWidth={141}>
        <StyledCrmLink
          href={salesforceContactUrl || salesforceLeadUrl}
          target="_blank"
          onMouseDown={() => handleCrmLinkTrack(Crm.salesforce)}
        >
          <StyledCrmExportButtonSmall data-automation="CrmExportSmallButton">
            <SWReactIcons iconName={Crm.salesforce} size="xs" iconColor={colorsPalettes.carbon[500]} />
          </StyledCrmExportButtonSmall>
        </StyledCrmLink>
      </Tooltip>
    );
  }

  if (hubspotContactUrl) {
    return (
      <Tooltip placement="top-left" content={translate('contact_card.open_in_hs')} minWidth={141}>
        <StyledCrmLink href={hubspotContactUrl} target="_blank" onMouseDown={() => handleCrmLinkTrack(Crm.hubspot)}>
          <StyledCrmExportButtonSmall data-automation="CrmExportSmallButton">
            <SWReactIcons iconName={Crm.hubspot} size="xs" iconColor={colorsPalettes.carbon[500]} />
          </StyledCrmExportButtonSmall>
        </StyledCrmLink>
      </Tooltip>
    );
  }

  if (!isHbIntegrated && !isSfIntegrated) {
    return <NotConnectedCrmButton handleEnableExportTrack={handleEnableExportTrack} />;
  }

  if (isHbIntegrated && !isSfIntegrated) {
    if (!isHbExportEnabled) {
      return <InactiveCrmExportButton handleEnableExportTrack={handleEnableExportTrack} crm={Crm.hubspot} />;
    }
    return <CrmExportContainer crm={Crm.hubspot} contact={contact} />;
  }

  if (isSfIntegrated && !isHbIntegrated) {
    if (!isSfExportEnabled) {
      return <InactiveCrmExportButton handleEnableExportTrack={handleEnableExportTrack} crm={Crm.salesforce} />;
    }
    return <CrmExportContainer crm={Crm.salesforce} contact={contact} />;
  }

  if (isHbIntegrated && isSfIntegrated) {
    if (!isSfExportEnabled && !isHbExportEnabled) {
      return <InactiveCrmExportButton handleEnableExportTrack={handleEnableExportTrack} />;
    }

    if (isSfExportEnabled && !isHbExportEnabled) {
      return <CrmExportContainer crm={Crm.salesforce} contact={contact} />;
    }

    if (!isSfExportEnabled && isHbExportEnabled) {
      return <CrmExportContainer crm={Crm.hubspot} contact={contact} />;
    }

    if (isHbIntegrated && isSfIntegrated && isSfExportEnabled && isHbExportEnabled) {
      return <CrmExportContainer crm={Crm.salesforce} contact={contact} />;
    }
  }

  return null;
};
